/* eslint-disable react-hooks/exhaustive-deps */
import { faCircleQuestion } from '@fortawesome/free-regular-svg-icons';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { MouseEventHandler, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ReactComponent as InfoIcon } from '../../assets/images/info.svg';
import CustomToolTip from '../../core/CustomToolTip/CustomToolTip';

import { DailyParamViewModelValueResult } from '../../api';
import {
	getApiDailyParamName,
	getApiPriceCalculatorCalculatorId,
	getApiRegionCountries,
	postApiTaricTaricNomenclature,
} from '../../api/interconectar-api';
import { useAuth } from '../../context/AuthContext';
import { useVideo } from '../../context/VideoContext';
import i18n from '../../i18n';
import CircularLoading from '../../shared/CircularLoading/CircularLoading';
import PopUp from '../../shared/PopUp/PopUp';
import { formatNumber, numberRegex } from '../../utils/helpers';
import IconCircleCheck from '../Icons/IconCircleCheck';
import IconCircleExclamation from '../Icons/IconCircleExclamation';
import { IconCkeckSolid } from '../Icons/IconCkeckSolid';
import IconError from '../Icons/IconError';
import IconPro from '../Icons/IconPro';
import './PriceCalculator.css';
import { Country, FormOneValues } from './PriceCalculator.types';
import { useRemainingCalculations } from '../../hooks/useRemainingCalculations';
import { IconDropDownArrow } from '../Icons';

const PriceCalculatorStepOne = ({
	handleClickOpen,
	handleContinueClick,
	selectedItemId,
}: {
	handleClickOpen: MouseEventHandler<HTMLDivElement>;
	handleContinueClick: () => void;
	selectedItemId?: string | null;
}) => {
	const { state } = useAuth();
	const {
		data: remainingCalcs,
		loading: loadingCalcs,
		error: errorCalcs,
		refetch: refetchRemainingCalcs,
	} = useRemainingCalculations(process.env.REACT_APP_PRICE_CALCULATOR_ID || '353');

	const [loadingSelectedId, setLoadingSelectedId] = useState<boolean>(false);
	const [errorSelectedId, setErrorSelectedId] = useState<string | null>(null);

	const [newCountries, setCountries] = useState<any[]>([]); // Estado para almacenar los países
	const [loadingCountries, setLoadingCountries] = useState<boolean>(true); // Estado para gestionar la carga
	const [errorCountries, setErrorCountries] = useState<string | null>(null); // Estado para manejar errores

	const { t } = useTranslation();
	const { setVideoName } = useVideo();
	const [formOneValues, setFormOneValues] = useState<any>();

	const lang = i18n.language || 'en'; // Idioma actual o inglés por defecto

	const [dataDaily, setDailyData] = useState<DailyParamViewModelValueResult | null>(null);
	const [errorDaily, setDailyError] = useState<string | null>(null);
	const [loadingDaily, setLoadingDaily] = useState<boolean>(true); // Estado de loading

	const [tarifDescription, setTarifDescription] = useState<string | null>(null);

	const [tarifCode, setTarifCode] = useState(() => {
		const formOne = localStorage.getItem('formOne');
		if (formOne) {
			const parsedForm = JSON.parse(formOne);
			return parsedForm.tarifCode || '';
		}
		return '';
	});

	const [isPopupTarifOpen, setIsPopupTarifOpen] = useState(false);
	const [isValidTarif, setIsValidTarif] = useState(false); // Estado de validación
	const [isLoadingTarif, setIsLoadingTarif] = useState(false); // Estado de carga

	useEffect(() => {
		const controller = new AbortController();
		setLoadingDaily(true);

		getApiDailyParamName('ParidadEuDolar', controller.signal)
			.then((response) => {
				if ('result' in response && 'status' in response) {
					setDailyData(response as DailyParamViewModelValueResult);
				} else {
					throw new Error(t('price_calculator.step_1.error_message_paridad_1'));
				}
				setDailyError(null);
			})
			.catch((err) => {
				setDailyError(err.message || t('price_calculator.step_1.error_message_paridad_2'));
			})
			.finally(() => {
				setLoadingDaily(false);
			});

		return () => controller.abort(); // Cancelar la petición al desmontar
	}, []);

	const emptyForm = {
		name: '',
		country: '',
		countryOriginId: '',
		originCode: '',
		euroDollarRate:
			state?.isAuthenticated && remainingCalcs && remainingCalcs.remaining ? dataDaily?.result?.value : '',
		unitFobUsd: '',
		productWeightKg: '',
		tarifCode: '',
		quantityGoods: '',
	};

	const defaultValues = {
		name: formOneValues?.name || '',
		country: formOneValues?.country || '',
		countryOriginId: formOneValues?.countryOriginId || '',
		originCode: formOneValues?.originCode || '',
		euroDollarRate:
			state?.isAuthenticated && remainingCalcs && remainingCalcs.remaining > 0
				? formOneValues?.euroDollarRate ||
					(localStorage.getItem('formOne')
						? JSON.parse(localStorage.getItem('formOne')!).euroDollarRate
						: dataDaily?.result?.value || '')
				: '',
		unitFobUsd: formOneValues?.unitFobUsd || '',
		productWeightKg: formOneValues?.productWeightKg || '',
		tarifCode: formOneValues?.tarifCode || '',
		quantityGoods: formOneValues?.quantityGoods || '',
	};

	const {
		register,
		handleSubmit,
		reset,
		setValue,
		setError,
		clearErrors,
		formState: { errors },
	} = useForm({
		defaultValues,
	});

	// Add this helper function after the useForm hook
	const handleInputChangeWithErrorClear = (
		fieldName: keyof FormOneValues,
		value: string,
		additionalLogic?: () => void,
	) => {
		// Clear error for this field when user starts typing
		if (errors[fieldName]) {
			clearErrors(fieldName);
		}

		// Execute additional logic if provided
		if (additionalLogic) {
			additionalLogic();
		}
	};

	// Restaurar la carga inicial de datos
	useEffect(() => {
		const formOne = localStorage.getItem('formOne') ? JSON.parse(localStorage.getItem('formOne')!) : '';
		if (Object.keys(formOne).length) {
			setFormOneValues(formOne);
			// Actualizar los valores del formulario sin usar reset
			Object.entries(formOne).forEach(([key, value]) => {
				setValue(key as keyof FormOneValues, value);
			});
		} else {
			const initialForm = {
				...emptyForm,
				euroDollarRate:
					state?.isAuthenticated && remainingCalcs && remainingCalcs.remaining && dataDaily?.result?.value
						? dataDaily.result.value
						: '',
				userSubscriptionId: remainingCalcs?.userSubscriptionId || null,
			};
			setFormOneValues(initialForm);
			// Actualizar los valores del formulario sin usar reset
			Object.entries(initialForm).forEach(([key, value]) => {
				setValue(key as keyof FormOneValues, value);
			});
		}
	}, [dataDaily, state?.isAuthenticated, remainingCalcs, setValue]);

	const onSubmit = (data: any) => {
		// Add userSubscriptionId to form data if available
		const formData = {
			...data,
			userSubscriptionId: remainingCalcs?.userSubscriptionId || null,
		};

		localStorage.setItem('formOne', JSON.stringify(formData));
		setFormOneValues(formData);
		console.log('Formulario guardado, bandera formOneChanged mantenida');
		handleContinueClick();
	};

	setVideoName('firstVideo');

	useEffect(() => {
		window.scrollTo({ top: 0, behavior: 'smooth' });
	}, []);

	// Load data when selectedItemId changes
	useEffect(() => {
		if (state?.isAuthenticated) {
			refetchRemainingCalcs();
		}
	}, [state?.isAuthenticated, selectedItemId]);

	useEffect(() => {
		if (selectedItemId) {
			loadSelectedItemData(selectedItemId);
		}
	}, [selectedItemId]);

	const loadSelectedItemData = async (id: string) => {
		setLoadingSelectedId(true);
		setErrorSelectedId(null);

		if (!state?.isAuthenticated) {
			setLoadingSelectedId(false);

			return;
		}

		try {
			const response = await getApiPriceCalculatorCalculatorId(id);
			reset();

			if (response && response.result) {
				const updatedData = {
					...response.result,
					name: response.result.name,
					country: response.result.countryOriginId,
					countryOriginId: response.result.countryOriginId,
					euroDollarRate: response.result.euroDollarRate ?? dataDaily?.result?.value,
					unitFobUsd: response.result.unitFobUsd,
					productWeightKg: response.result.productWeightKg,
					tarifCode: response.result.tarifCode,
					quantityGoods: response.result.quantityGoods,
				};

				setFormOneValues(updatedData);
				setTarifCode(response.result.tarifCode || '');

				if (response.result.meansTransportation) {
					const transportType = response.result.meansTransportation.toLowerCase();
					let specificData = {};

					switch (transportType) {
						case 'maritime':
							specificData = {
								portDestinationId: response.result.portDestinationId,
								meansTransportation: 'maritimo',
								containerId: response.result.containerId,
								internationalFreightValueUsd: response.result.internationalFreightValueUsd,
							};
							break;

						case 'multimodal':
							specificData = {
								portDestinationId: response.result.portDestinationId,
								meansTransportation: 'multimodal',
								internationalFreightValueUsd: response.result.internationalFreightValueUsd,
							};
							break;

						case 'air':
							specificData = {
								portDestinationId: response.result.portDestinationId,
								meansTransportation: 'aereo',
								airTotalWeightPlusPackaging: response.result.airTotalWeightPlusPackaging,
								airTarifCode: response.result.airTarifCode,
								internationalFreightValueUsd: response.result.internationalFreightValueUsd,
								airportId: response.result.airportId,
							};
							break;

						default:
							console.warn('Transportation type not recognized:', transportType);
							break;
					}

					const updatedData = {
						...response.result,
						portDestinationId: response.result.portDestinationId,
						meansTransportation: response.result.meansTransportation,
						internationalFreightValueUsd: response.result.internationalFreightValueUsd,
					};
					const updatedData3 = {
						...response.result,
						portExpensesUsd: response.result.portExpensesUsd,
						customersExpensesValueUsd: response.result.customersExpensesValueUsd,
						countryDestinationId: response.result.countryDestinationId,
						adValoremTax: response.result.adValoremTax,
						taxDeterminedByWeight: response.result.taxDeterminedByWeight,
						costTransportationPortWarehouse: response.result.costTransportationPortWarehouse,
						country: response.result.countryOriginId,
						countryOriginId: response.result.countryOriginId,
						importerTruckType: response.result.importerTruckType,
						importerCity: response.result.importerCity,
					};
					const updatedData4 = {
						wholesalerMargin: response.result.wholesalerMargin,
						quantityMerchandiseRetailer: response.result.quantityMerchandiseRetailer,
						costTransportationWholesalerRetailerUsd: response.result.costTransportationWholesalerRetailerUsd,
						marketingSegment: response.result.marketingSegment,
						retailerMargin: response.result.retailerMargin,
						retailTruckType: response.result.retailTruckType,
						retailerCity: response.result.retailerCity,
					};

					// Save data to localStorage
					if (Object.keys(specificData).length > 0) {
						localStorage.setItem('formTwo', JSON.stringify(specificData));
					}
					localStorage.setItem('formOne', JSON.stringify(updatedData));
					localStorage.setItem('formThree', JSON.stringify(updatedData3));
					localStorage.setItem('formFour', JSON.stringify(updatedData4));
				}

				localStorage.setItem('selectedItemData', JSON.stringify(updatedData));

				// Actualizar los valores del formulario sin sobrescribir ediciones en curso
				Object.entries(updatedData).forEach(([key, value]) => {
					setValue(key as keyof FormOneValues, value);
				});
			} else {
				setErrorSelectedId(t('price_calculator.step_1.error_message_selected_id_1'));
			}
		} catch (err) {
			setErrorSelectedId(t('price_calculator.step_1.error_message_selected_id_2'));
		} finally {
			setLoadingSelectedId(false);
		}
	};

	useEffect(() => {
		// Llamada a la API
		const fetchCountries = async () => {
			setLoadingCountries(true);
			try {
				const data = await getApiRegionCountries();
				if (data.results) {
					setCountries([...data.results]);
				}
			} catch (error) {
				setErrorCountries(t('price_calculator.step_1.error_message_countries'));
			} finally {
				setLoadingCountries(false);
			}
		};

		fetchCountries();
	}, []);

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setTarifCode(value);
		const formattedValue = formatNumber(value);
		setFormOneValues((prev: FormOneValues) => {
			const updatedValues = { ...prev, tarifCode: formattedValue };
			localStorage.setItem('tarifCode', formattedValue);
			// Guardar inmediatamente en localStorage para persistir los cambios
			localStorage.setItem('formOne', JSON.stringify(updatedValues));
			return updatedValues;
		});
		setValue('tarifCode', formattedValue, { shouldValidate: false });
	};

	const handleOpenTarifPopup = async () => {
		if (!tarifCode) {
			setError('tarifCode', { type: 'required', message: t('price_calculator.step_1.error_message') });
			return;
		}

		setIsLoadingTarif(true);
		clearErrors('tarifCode');

		// formatear el tarifCode para que solo sea numeros
		const formattedTarifCode = tarifCode.replace(/[^0-9]/g, '');

		try {
			const response = await postApiTaricTaricNomenclature({ goodsCode: formattedTarifCode });
			if (response?.results && response.results.length > 0) {
				// Formatear las descripciones en estructura jerárquica con divs y spans
				const formattedDescriptions = response.results
					.map((item) => {
						if (!item?.goodsCode || !item?.hierPos) return '';
						const indent = item.indent || '';
						const fullCode = item.goodsCode.split(' ')[0];
						// Calcula cuántos dígitos mostrar basado en hierPos
						const digitsToShow = Math.ceil(item.hierPos / 2) * 2;
						const code = fullCode.substring(0, digitsToShow);
						const itemDescription = lang === 'en' ? item.descriptionEN : item.descriptionES;
						return `<div class="tariff-item">
						<span class="tariff-code">${code}</span>
						<span class="tariff-description">${indent}${itemDescription}</span>
					</div>`;
					})
					.join('');

				if (formattedDescriptions) {
					setTarifDescription(formattedDescriptions || '');
					setIsValidTarif(true);
					setValue('tarifCode', formattedTarifCode, { shouldValidate: true });
					setIsPopupTarifOpen(true);
				} else {
					setIsValidTarif(false);
					setError('tarifCode', { type: 'invalid', message: t('price_calculator.step_1.error_message') });
					setIsPopupTarifOpen(true);
				}
			} else {
				setIsValidTarif(false);
				setError('tarifCode', { type: 'invalid', message: t('price_calculator.step_1.error_message') });
				setIsPopupTarifOpen(true);
			}
		} catch (error) {
			setIsValidTarif(false);
			setError('tarifCode', { type: 'invalid', message: t('price_calculator.step_1.error_message') });
			setIsPopupTarifOpen(true);
			console.error('Error fetching tarif description:', error);
		} finally {
			setIsLoadingTarif(false);
		}
	};

	const handleCloseTarifPopup = () => {
		setIsPopupTarifOpen(false);
	};

	if (loadingSelectedId || loadingCalcs) {
		return <CircularLoading />;
	}

	if (loadingCountries) {
		return <CircularLoading />;
	}

	if (loadingDaily) {
		return <CircularLoading />;
	}

	if (errorCountries) {
		return (
			<div className="errorcalculator">
				<div className="errorcalculator__icon">
					<IconError />
				</div>
				<p className="errorcalculator__txt">{errorCountries}</p>
			</div>
		);
	}

	if (errorSelectedId) {
		return (
			<div className="errorcalculator">
				<div className="errorcalculator__icon">
					<IconError />
				</div>
				<p className="errorcalculator__txt">{errorSelectedId}</p>
			</div>
		);
	}

	if (errorDaily) {
		return (
			<div className="errorcalculator">
				<div className="errorcalculator__icon">
					<IconError />
				</div>
				<p className="errorcalculator__txt">{errorDaily}</p>
			</div>
		);
	}

	return (
		<form onSubmit={handleSubmit(onSubmit)}>
			<div className="exporter-test__read-only">
				<div className="price-calculator__container-main">
					<div className="price-calculator___inner">
						<div className="row price-calculator__row mb-1">
							<div className="col-12">
								<div className="price-calculator__field-with-icon price-calculator-first label-container">
									<input
										type="text"
										className="price-calculator__textbox-general "
										value={!!formOneValues?.name ? formOneValues?.name : null}
										{...register('name', { required: true })}
										onChange={(e) => {
											handleInputChangeWithErrorClear('name', e.target.value, () => {
												setFormOneValues((prev: FormOneValues) => {
													const updatedValues = { ...prev, name: e.target.value };
													// Guardar inmediatamente en localStorage para persistir los cambios
													localStorage.setItem('formOne', JSON.stringify(updatedValues));
													return updatedValues;
												});
											});
										}}
									/>
									<label className="label-input">{t('price_calculator.step_1.name.placeholder')}</label>
									<div className="price-calculator-ml-lg">
										<CustomToolTip
											title=""
											tooltipText={t('price_calculator.step_1.name.tooltip')}>
											<FontAwesomeIcon
												icon={faCircleQuestion}
												className="font-size: 18px"
											/>
										</CustomToolTip>
									</div>
								</div>

								{!!errors?.name && (
									<div className="error-container">
										<InfoIcon fill="#F8A0D2" />
										<div className="error-message">{t('price_calculator.step_1.error_message')}</div>
									</div>
								)}
							</div>
						</div>
					</div>

					<div className="price-calculator___inner">
						<div className=" price-calculator___container">
							<div className="row">
								<div className="col-12">
									<span className="price-calculator__container-title">{t('price_calculator.step_1.subtitle')}</span>
								</div>
							</div>

							<div className="row price-calculator__row mb-4">
								<div className="col-12 col-sm-12 col-md-6 ">
									<div className="price-calculator__field-with-icon">
										<div className="label-container">
											<label className="label-input">{t('price_calculator.step_1.country.placeholder')}</label>

											<select
												{...register('country', { required: true })}
												onChange={(e) => {
													handleInputChangeWithErrorClear('country', e.target.value, () => {
														const selectedCountryId = e.target.value;
														// Find the selected country to get its code
														let selectedCountryCode = '';
														newCountries.forEach((region) => {
															const foundCountry = region.countries.find(
																(country: Country) => country.id === selectedCountryId,
															);
															if (foundCountry) {
																selectedCountryCode = foundCountry.code;
															}
														});

														setValue('country', selectedCountryId);
														setValue('countryOriginId', selectedCountryId);
														setValue('originCode', selectedCountryCode);
														setFormOneValues((prev: FormOneValues) => {
															const newValues = {
																...prev,
																country: selectedCountryId,
																countryOriginId: selectedCountryId,
																originCode: selectedCountryCode,
															};

															// Verificar si hay cambios en los valores del país
															// Convertir a string para comparación más robusta
															const prevCountryId = String(prev.countryOriginId || '');
															const selectedCountryIdStr = String(selectedCountryId || '');
															const prevOriginCode = String(prev.originCode || '');
															const selectedCountryCodeStr = String(selectedCountryCode || '');

															const hasChanges =
																prevCountryId !== selectedCountryIdStr || prevOriginCode !== selectedCountryCodeStr;

															// console.log('=== DEBUG COUNTRY CHANGE ===');
															// console.log('prev.countryOriginId (original):', prev.countryOriginId);
															// console.log('prevCountryId (converted):', prevCountryId);
															// console.log('selectedCountryId (original):', selectedCountryId);
															// console.log('selectedCountryIdStr (converted):', selectedCountryIdStr);
															// console.log('prev.originCode (original):', prev.originCode);
															// console.log('prevOriginCode (converted):', prevOriginCode);
															// console.log('selectedCountryCode (original):', selectedCountryCode);
															// console.log('selectedCountryCodeStr (converted):', selectedCountryCodeStr);
															// console.log('hasChanges:', hasChanges);

															if (hasChanges) {
																// console.log('Detectados cambios en el país, marcando formOneChanged');
																localStorage.setItem('formOneChanged', 'true');
																// Guardar inmediatamente en localStorage para persistir los cambios
																localStorage.setItem('formOne', JSON.stringify(newValues));
															} else if (selectedCountryId && selectedCountryId !== '') {
																// Si es la primera vez que se selecciona un país válido, también marcar como cambiado
																// console.log('Primera selección de país válido, marcando formOneChanged');
																localStorage.setItem('formOneChanged', 'true');
																localStorage.setItem('formOne', JSON.stringify(newValues));
															} else {
																// console.log('No se detectaron cambios en el país');
															}

															return newValues;
														});
													});
												}}
												className="label-container__input input-label-select">
												<option
													value=""
													disabled>
													{t('select.placeholder')}
												</option>
												{newCountries.map((region) => (
													<optgroup
														key={region.nameEs}
														label={region.nameEs}>
														{region.countries.map((country: Country) => (
															<option
																key={country.id}
																value={country.id}
																className="price-calculator__select-font">
																{country.name}
															</option>
														))}
													</optgroup>
												))}
											</select>
											<IconDropDownArrow className="input-label-select-arrow" />
										</div>

										<div className="price-calculator-ml-sm">
											<CustomToolTip
												title=""
												tooltipText={t('price_calculator.step_1.country.tooltip')}>
												<FontAwesomeIcon
													icon={faCircleQuestion}
													className="font-size: 18px"
												/>
											</CustomToolTip>
										</div>
									</div>
									{!!errors?.country && (
										<div className="error-container">
											<InfoIcon fill="#F8A0D2" />
											<div className="error-message">{t('price_calculator.step_1.error_message')}</div>
										</div>
									)}
								</div>

								<div className="col-12 col-sm-12 col-md-6 label-container container_input__pro">
									<input
										{...register('euroDollarRate', { required: true })}
										value={formOneValues?.euroDollarRate || ''}
										onChange={(e) => {
											handleInputChangeWithErrorClear('euroDollarRate', e.target.value, () => {
												const value = e.target.value;
												if (value === '' || numberRegex.test(value)) {
													const formattedValue = formatNumber(value);
													setFormOneValues((prev: FormOneValues) => {
														const updatedValues = { ...prev, euroDollarRate: formattedValue };
														// Guardar inmediatamente en localStorage para persistir los cambios
														localStorage.setItem('formOne', JSON.stringify(updatedValues));
														return updatedValues;
													});
												}
											});
										}}
										type="number"
										className="price-calculator__textbox-general-col2"
									/>
									<label className="label-input">{t('price_calculator.step_1.euroDollarRate.placeholder')}</label>

									<span className="label__pro">
										<IconPro
											width={13}
											height={10}
											fill="#BBC2C7"
										/>
										{t('price_calculator.label_pro.description')}
									</span>

									{!!errors?.euroDollarRate && (
										<div className="error-container">
											<InfoIcon fill="#F8A0D2" />
											<div className="error-message">{t('price_calculator.step_1.error_message')}</div>
										</div>
									)}
								</div>
							</div>

							<div className="row price-calculator__row mb-4">
								<div className="col-12 col-sm-12 col-md-6 ">
									<div className="price-calculator__field-with-icon">
										<div className="price-calculator__field-with-label">
											<div className="tarif-code_container ">
												<input
													{...register('tarifCode', {
														required: true,
													})}
													name="tarifCode"
													value={tarifCode}
													onChange={(e) => {
														handleInputChangeWithErrorClear('tarifCode', e.target.value, () => {
															const value = e.target.value;
															setTarifCode(value);
															const formattedValue = formatNumber(value);
															setFormOneValues((prev: FormOneValues) => {
																const updatedValues = { ...prev, tarifCode: formattedValue };
																localStorage.setItem('tarifCode', formattedValue);
																// Guardar inmediatamente en localStorage para persistir los cambios
																localStorage.setItem('formOne', JSON.stringify(updatedValues));
																return updatedValues;
															});
															setValue('tarifCode', formattedValue, { shouldValidate: false });
														});
													}}
													onPaste={(e) => {
														e.preventDefault();
														const pastedValue = e.clipboardData.getData('text');
														setTarifCode(pastedValue);
														const formattedValue = formatNumber(pastedValue);
														setFormOneValues((prev: FormOneValues) => {
															const updatedValues = { ...prev, tarifCode: formattedValue };
															// Guardar inmediatamente en localStorage para persistir los cambios
															localStorage.setItem('formOne', JSON.stringify(updatedValues));
															return updatedValues;
														});
														setValue('tarifCode', formattedValue, { shouldValidate: true });
														clearErrors('tarifCode');
													}}
													type="text"
													className="price-calculator__textbox-general-col2 tarif-code_input input-label-select"
												/>
												<label className="label-input">{t('price_calculator.step_1.tarifCode.placeholder')}</label>
												{state?.isAuthenticated && remainingCalcs && remainingCalcs.remaining ? (
													!isValidTarif ? (
														<button
															onClick={handleOpenTarifPopup}
															className="tarif-code_button"
															type="button">
															{isLoadingTarif ? (
																<CircularLoading />
															) : (
																<>{t('price_calculator.step_1.tarif_code_button')}</>
															)}
														</button>
													) : (
														<span
															className="tarif-code_button tarif-code_button-succes"
															onClick={handleOpenTarifPopup}>
															<IconCkeckSolid
																width={17}
																height={20}
																fill="#1A2228"
															/>
														</span>
													)
												) : null}

												{/* Popup */}
												<PopUp
													isOpen={isPopupTarifOpen}
													onClose={handleCloseTarifPopup}>
													{isLoadingTarif ? (
														<CircularLoading />
													) : isValidTarif ? (
														<div className="modal-tc">
															<div className="modal-tc_icon">
																<IconCircleCheck />
															</div>
															<h4 className="modal-tc_title">
																{t('price_calculator.step_1.modal_tarif_code.success_title')} {tarifCode}
															</h4>
															<div
																className="modal-tc_text"
																dangerouslySetInnerHTML={{ __html: tarifDescription || '' }}
															/>
															<button
																onClick={handleCloseTarifPopup}
																className="modal-tc_button"
																type="button">
																{t('price_calculator.step_1.modal_tarif_code.success_button')}
															</button>
														</div>
													) : (
														<div className="modal-tc">
															<div className="modal-tc_icon">
																<IconCircleExclamation
																	width={42}
																	height={42}
																/>
															</div>
															<h4 className="modal-tc_title">
																{t('price_calculator.step_1.modal_tarif_code.error_title')} {tarifCode}
															</h4>
															<p className="modal-tc_text">
																{t('price_calculator.step_1.modal_tarif_code.error_description')}
															</p>
															<button
																onClick={handleCloseTarifPopup}
																className="modal-tc_button"
																type="button">
																{t('price_calculator.step_1.modal_tarif_code.error_button')}
															</button>
														</div>
													)}
												</PopUp>
											</div>
											<a
												href={`https://ec.europa.eu/taxation_customs/dds2/taric/taric_consultation.jsp?Lang=${lang}`}
												target="_blank"
												className="price-calculator__field-label">
												{t('price_calculator.step_1.tarif_code_label')}
											</a>
										</div>
										<div className="price-calculator-ml-sm price-calculator-mt">
											<CustomToolTip
												title=""
												tooltipText={
													<>
														{t('price_calculator.step_1.tarifCode.tooltip_first')}{' '}
														<a
															target="_blank"
															rel="noreferrer"
															href="https://ec.europa.eu/taxation_customs/dds2/taric/taric_consultation.jsp?Lang=es&Expand=true&SimDate=20230823#afterForm"
															className="price-calc__tootip-link">
															{t('price_calculator.step_1.tarifCode.tooltip_link')}
														</a>{' '}
														{t('price_calculator.step_1.tarifCode.tooltip_last')}
													</>
												}>
												<FontAwesomeIcon
													icon={faCircleQuestion}
													className="font-size: 18px"
												/>
											</CustomToolTip>
										</div>
									</div>
									{!!errors?.tarifCode && (
										<div className="error-container">
											<InfoIcon fill="#F8A0D2" />
											<div className="error-message">{t('price_calculator.step_1.error_message')}</div>
										</div>
									)}
								</div>
								<div className="col-12 col-sm-12 col-md-6 label-container">
									<input
										{...register('productWeightKg', { required: true })}
										value={!!formOneValues?.productWeightKg ? formOneValues?.productWeightKg : ''}
										onChange={(e) => {
											handleInputChangeWithErrorClear('productWeightKg', e.target.value, () => {
												if (e.target.value === '' || numberRegex.test(e.target.value)) {
													setFormOneValues((prev: FormOneValues) => {
														const updatedValues = { ...prev, productWeightKg: formatNumber(e.target.value) };
														// Guardar inmediatamente en localStorage para persistir los cambios
														localStorage.setItem('formOne', JSON.stringify(updatedValues));
														return updatedValues;
													});
												}
											});
										}}
										type="number"
										className="price-calculator__textbox-general-col2"
									/>
									<label className="label-input">{t('price_calculator.step_1.productWeightKg.placeholder')}</label>

									{!!errors?.productWeightKg && (
										<div className="error-container">
											<InfoIcon fill="#F8A0D2" />
											<div className="error-message">{t('price_calculator.step_1.error_message')}</div>
										</div>
									)}
								</div>
							</div>

							<div className="row price-calculator__row mb-4">
								<div className="col-12 col-sm-12 col-md-6 ">
									<div className="price-calculator__field-with-icon label-container">
										<input
											{...register('unitFobUsd', { required: true })}
											value={!!formOneValues?.unitFobUsd ? formOneValues?.unitFobUsd : ''}
											onChange={(e) => {
												handleInputChangeWithErrorClear('unitFobUsd', e.target.value, () => {
													if (e.target.value === '' || numberRegex.test(e.target.value)) {
														setFormOneValues((prev: FormOneValues) => {
															const updatedValues = { ...prev, unitFobUsd: formatNumber(e.target.value) };
															// Guardar inmediatamente en localStorage para persistir los cambios
															localStorage.setItem('formOne', JSON.stringify(updatedValues));
															return updatedValues;
														});
													}
												});
											}}
											type="number"
											className="price-calculator__textbox-general-col2"
										/>
										<label className="label-input">{t('price_calculator.step_1.unitFobUsd.placeholder')}</label>
										<div className="price-calculator-ml-sm">
											<CustomToolTip
												title=""
												tooltipText={t('price_calculator.step_1.unitFobUsd.tooltip')}>
												<FontAwesomeIcon
													icon={faCircleQuestion}
													className="font-size: 18px"
												/>
											</CustomToolTip>
										</div>
									</div>
									{!!errors?.unitFobUsd && (
										<div className="error-container">
											<InfoIcon fill="#F8A0D2" />
											<div className="error-message">{t('price_calculator.step_1.error_message')}</div>
										</div>
									)}
								</div>
								<div className="col-12 col-sm-12 col-md-6">
									<div className="price-calculator__field-with-icon label-container">
										<input
											{...register('quantityGoods', { required: true })}
											value={!!formOneValues?.quantityGoods ? formOneValues?.quantityGoods : ''}
											onChange={(e) => {
												handleInputChangeWithErrorClear('quantityGoods', e.target.value, () => {
													if (e.target.value === '' || numberRegex.test(e.target.value)) {
														setFormOneValues((prev: FormOneValues) => {
															const updatedValues = { ...prev, quantityGoods: formatNumber(e.target.value) };
															// Guardar inmediatamente en localStorage para persistir los cambios
															localStorage.setItem('formOne', JSON.stringify(updatedValues));
															return updatedValues;
														});
													}
												});
											}}
											type="number"
											className="price-calculator__textbox-general-col2"
										/>
										<label className="label-input">{t('price_calculator.step_1.quantityGoods.placeholder')}</label>
										<div className="price-calculator-ml-sm">
											<CustomToolTip
												title=""
												tooltipText={
													state?.isAuthenticated && remainingCalcs && remainingCalcs.remaining
														? t('price_calculator.step_1.quantityGoods.tooltipPro')
														: t('price_calculator.step_1.quantityGoods.tooltip')
												}>
												<FontAwesomeIcon
													icon={faCircleQuestion}
													className="font-size: 18px"
												/>
											</CustomToolTip>
										</div>
									</div>
									{!!errors?.quantityGoods && (
										<div className="error-container">
											<InfoIcon fill="#F8A0D2" />
											<div className="error-message">{t('price_calculator.step_1.error_message')}</div>
										</div>
									)}
								</div>
							</div>
						</div>
					</div>
					<div className="price-calculator__container-footer">
						<div
							className="price-calculator__footer-link"
							onClick={handleClickOpen}>
							{t('price_calculator.footer.link')}
						</div>
						<button
							type="submit"
							className="price-calc__next">
							<div className="btn-test-next-text">{t('price_calculator.footer.button')}</div>
							<FontAwesomeIcon
								icon={faArrowRight}
								color="black"
							/>
						</button>
					</div>
				</div>
			</div>
		</form>
	);
};

export default PriceCalculatorStepOne;
